/*
 * Wishlist — browser-based (localStorage) wishlist for the AlCell theme.
 *
 * Shopify has no native wishlist, so this keeps the saved list in the
 * visitor's browser. It works for guests and logged-in customers alike,
 * needs no app or backend, and persists across page loads on the same
 * device/browser. (It does not sync across devices — see README note in
 * the PR description for the metafield/app upgrade path.)
 *
 * Storage shape (localStorage key "alcell:wishlist"):
 *   [{ id, handle, title, url, image, price, compare, available, variant }]
 *   price/compare are integer cents (Shopify money), formatted on render.
 *
 * Wiring (data attributes, no per-page JS needed):
 *   [data-wishlist-toggle]  heart button that adds/removes a product.
 *                           Reads data-wishlist-* attributes for the snapshot.
 *   [data-wishlist-count]   element whose text = number of saved items.
 *   [data-wishlist-grid]    container the wishlist page renders cards into.
 *   [data-wishlist-empty]   empty-state element shown when the list is empty.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'alcell:wishlist';

  /* ---------- storage ---------- */

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* storage full / disabled — fail silently */
    }
  }

  function has(id) {
    id = String(id);
    return read().some(function (item) {
      return String(item.id) === id;
    });
  }

  function add(item) {
    var list = read();
    if (list.some(function (i) { return String(i.id) === String(item.id); })) return list;
    list.push(item);
    write(list);
    return list;
  }

  function remove(id) {
    id = String(id);
    var list = read().filter(function (item) {
      return String(item.id) !== id;
    });
    write(list);
    return list;
  }

  /* ---------- helpers ---------- */

  // Format integer cents as "R 1,234" — mirrors the price-rand Liquid snippet
  // (whole rands, comma thousands separators, no decimals).
  function formatRand(cents) {
    var rands = Math.floor((parseInt(cents, 10) || 0) / 100);
    return 'R ' + String(rands).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function snapshotFromButton(btn) {
    return {
      id: btn.getAttribute('data-wishlist-id'),
      handle: btn.getAttribute('data-wishlist-handle') || '',
      title: btn.getAttribute('data-wishlist-title') || '',
      url: btn.getAttribute('data-wishlist-url') || '',
      image: btn.getAttribute('data-wishlist-image') || '',
      price: btn.getAttribute('data-wishlist-price') || '',
      compare: btn.getAttribute('data-wishlist-compare') || '',
      available: btn.getAttribute('data-wishlist-available') !== 'false',
      variant: btn.getAttribute('data-wishlist-variant') || ''
    };
  }

  /* ---------- UI sync ---------- */

  function updateCounts() {
    // Count only what the wishlist page will actually render. Unavailable items
    // (out of stock, or unpublished so their page 404s) are deliberately KEPT in
    // storage so they come back when restocked — but counting them made the
    // badge read "1" while the page showed the empty state.
    var count = read().filter(function (item) { return item.available !== false; }).length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count > 0 ? String(count) : '';
      if (count > 0) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }

  function syncButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var active = has(btn.getAttribute('data-wishlist-id'));
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      var label = active ? 'Remove from wishlist' : 'Add to wishlist';
      btn.setAttribute('aria-label', label);
      btn.setAttribute('title', active ? 'In your wishlist' : 'Add to Wishlist');
      var text = btn.querySelector('[data-wishlist-label]');
      if (text) text.textContent = active ? 'In your List' : 'Add to List';
    });
  }

  function refreshAll() {
    updateCounts();
    syncButtons();
  }

  /* ---------- toggle (event delegation) ---------- */

  function onToggleClick(e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var id = btn.getAttribute('data-wishlist-id');
    if (!id) return;

    if (has(id)) {
      remove(id);
    } else {
      add(snapshotFromButton(btn));
    }

    refreshAll();
    renderPage(); // no-op when not on the wishlist page
  }

  /* ---------- wishlist page render ---------- */

  function cardHTML(item) {
    var hasCompare = item.compare && parseInt(item.compare, 10) > parseInt(item.price, 10);
    var media = item.image
      ? '<img src="' + esc(item.image) + '" alt="' + esc(item.title) + '" loading="lazy">'
      : '<div class="wishlist-card__placeholder"></div>';

    var priceRow =
      '<div class="wishlist-card__price-row">' +
        '<span class="wishlist-card__price' + (hasCompare ? ' wishlist-card__price--sale' : '') + '">' +
          esc(formatRand(item.price)) +
        '</span>' +
        (hasCompare ? '<span class="wishlist-card__compare">' + esc(formatRand(item.compare)) + '</span>' : '') +
      '</div>';

    var cta = item.available
      ? '<button type="button" class="wishlist-card__add" data-wishlist-add' +
          ' data-variant="' + esc(item.variant) + '" data-url="' + esc(item.url) + '">Add to Cart</button>'
      : '<span class="wishlist-card__soldout">Sold out</span>';

    // Out-of-stock products are unpublished from the Online Store (their page
    // 404s). Keep the saved item in the list, but render its media + title as
    // NON-clickable while unavailable so it can never become a 404 link; it turns
    // back into a link once the product is restocked and republished.
    var mediaEl = item.available
      ? '<a class="wishlist-card__media" href="' + esc(item.url) + '" aria-label="' + esc(item.title) + '">' + media + '</a>'
      : '<div class="wishlist-card__media wishlist-card__media--unavailable" role="img" aria-label="' + esc(item.title) + '">' + media + '</div>';
    var titleEl = item.available
      ? '<a class="wishlist-card__title" href="' + esc(item.url) + '">' + esc(item.title) + '</a>'
      : '<span class="wishlist-card__title wishlist-card__title--unavailable">' + esc(item.title) + '</span>';

    return '' +
      '<article class="wishlist-card" data-wishlist-item data-id="' + esc(item.id) + '">' +
        '<button type="button" class="wishlist-card__remove" data-wishlist-remove data-id="' + esc(item.id) + '" aria-label="Remove from wishlist" title="Remove">' +
          '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button>' +
        mediaEl +
        '<div class="wishlist-card__body">' +
          titleEl +
          priceRow +
          cta +
        '</div>' +
      '</article>';
  }

  // Session cache of verified availability by handle, so re-renders (e.g. after a
  // remove) don't re-hit the network or flicker.
  var availabilityCache = Object.create(null);

  // Refresh an item's availability against the live product. Out-of-stock items
  // are unpublished, so /products/<handle>.js 404s — we mark those unavailable
  // (never dropped: the shopper saved them) so the card renders non-clickable.
  // Network errors leave the stored value untouched.
  function verifyItem(item) {
    if (!item || !item.handle) return Promise.resolve(item);
    if (item.handle in availabilityCache) {
      item.available = availabilityCache[item.handle];
      return Promise.resolve(item);
    }
    return fetch('/products/' + encodeURIComponent(item.handle) + '.js', {
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      if (!r.ok) { item.available = false; availabilityCache[item.handle] = false; return item; }
      return r.json().then(function (p) {
        item.available = !!(p && p.available);
        availabilityCache[item.handle] = item.available;
        if (p && typeof p.price === 'number') item.price = p.price;
        if (p && typeof p.compare_at_price === 'number') item.compare = p.compare_at_price || 0;
        return item;
      }).catch(function () { return item; });
    }).catch(function () { return item; });
  }

  function renderPage() {
    var grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return; // not on the wishlist page

    var list = read();
    var empty = document.querySelector('[data-wishlist-empty]');
    var countEl = document.querySelector('[data-wishlist-page-count]');

    if (!list.length) {
      if (countEl) countEl.textContent = '0 items';
      grid.innerHTML = '';
      grid.setAttribute('hidden', '');
      if (empty) empty.removeAttribute('hidden');
      return;
    }

    // Verify availability, then show ONLY in-stock, still-listed products —
    // out-of-stock items are unpublished (their page 404s) and must never appear
    // as offer cards anywhere, the wishlist included. Unavailable items are KEPT
    // in storage (not deleted), so they reappear automatically once the product
    // is restocked and republished; the count reflects what's shown.
    Promise.all(list.map(verifyItem)).then(function (verified) {
      write(verified);
      // Verification is the only place stale availability gets corrected, so the
      // header badge has to be re-read from the fresh flags here. Without this
      // the wishlist page shows its own "1" badge above an empty state.
      updateCounts();
      var shown = verified.filter(function (i) { return i.available; });
      if (countEl) countEl.textContent = shown.length === 1 ? '1 item' : shown.length + ' items';
      if (shown.length) {
        if (empty) empty.setAttribute('hidden', '');
        grid.innerHTML = shown.map(cardHTML).join('');
        grid.removeAttribute('hidden');
      } else {
        grid.innerHTML = '';
        grid.setAttribute('hidden', '');
        if (empty) empty.removeAttribute('hidden');
      }
    });
  }

  /* ---------- wishlist page actions ---------- */

  function onPageClick(e) {
    var removeBtn = e.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      e.preventDefault();
      remove(removeBtn.getAttribute('data-id'));
      refreshAll();
      renderPage();
      return;
    }

    var addBtn = e.target.closest('[data-wishlist-add]');
    if (addBtn) {
      e.preventDefault();
      var variant = addBtn.getAttribute('data-variant');
      var url = addBtn.getAttribute('data-url') || '/';
      if (!variant) { window.location.href = url; return; }

      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';
      var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
      fetch(addUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant, quantity: 1 }] })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add failed');
          window.location.href = cartUrl;
        })
        .catch(function () {
          // Variant gone or selection required — fall back to the product page.
          window.location.href = url;
        });
    }
  }

  /* ---------- product-card "Add to Cart" (single-variant fast-add) ----------
   * Cards across the site carry an [data-card-atc] button. For single-variant
   * products it AJAX-adds the variant and sends the visitor to the cart; for
   * multi-variant products (data-multi) it just follows the link to the product
   * page so options can be chosen. The <a href> is the no-JS / multi fallback. */

  function onCardAtcClick(e) {
    var btn = e.target.closest('[data-card-atc]');
    if (!btn) return;

    // Multi-variant or no variant id → let the link navigate to the product page.
    if (btn.hasAttribute('data-multi')) return;
    var variant = btn.getAttribute('data-variant');
    if (!variant) return;

    e.preventDefault();
    if (btn.getAttribute('aria-busy') === 'true') return; // ignore double-clicks

    var url = btn.getAttribute('data-url') || '/';
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    btn.setAttribute('aria-busy', 'true');
    fetch(addUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variant, quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        window.location.href = cartUrl;
      })
      .catch(function () {
        // Variant gone or a selection is required — fall back to the product page.
        window.location.href = url;
      });
  }

  /* ---------- init ---------- */

  function init() {
    document.addEventListener('click', onToggleClick);
    document.addEventListener('click', onCardAtcClick);
    var grid = document.querySelector('[data-wishlist-grid]');
    if (grid) {
      document.addEventListener('click', onPageClick);
      renderPage();
    }
    refreshAll();

    // Keep tabs in sync when the list changes elsewhere.
    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_KEY) { refreshAll(); renderPage(); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Small public surface in case other scripts want it.
  window.Wishlist = { read: read, has: has, add: add, remove: remove, refresh: function () { refreshAll(); renderPage(); } };
})();
